import { Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { APP_CONSTANTS } from 'src/app/app.constants';
import { WellService } from './well.service';
import { Well } from '../interfaces/wellprotocol.interface';
import { applyApiToGui } from '../scheme/scheme.utils';

@Injectable()
export class WellServiceHMI extends WellService {
  // Id of the well most recently opened via setCurrentWellById, which the GET_WELL retry re-asks.
  private _lastWellFetchId: string | null = null;
  // A freshly activated controller has no well.json until it syncs with the backend, and answers
  // GET_WELL with an empty well (no id, no purpose) until then — possibly as an error reply, since
  // OLDWS keeps the body when the reply then fails validation. Either kind leaves the { id } stub in
  // place and re-asks after this delay, until a well with a purpose arrives.
  private readonly _wellRetryDelayMs = 5000;
  private _wellRetryTimer: ReturnType<typeof setTimeout> | null = null;

  /** GET_WELL is subscribed through subscribeWsManaged rather than subscribeWs: an error reply is
   *  retried every _wellRetryDelayMs, and the automatic error toast subscribeWs raises — which
   *  never times out — would pile up on the touchscreen. The placeholder reports the failure
   *  instead (see wellLoadIssue). */
  constructor() {
    super();
    this.ws.subscribeWsManaged(APP_CONSTANTS.RESPONSE_MESSAGES.GET_WELL, (msg) => {
      const raw = msg.body?.['well'] as Well | undefined;
      if (msg.header['status'] !== 4000) {
        this._wellLoadIssue.set('failed');
        this.scheduleWellRetry();
      } else if (!raw?.purpose) {
        this._wellLoadIssue.set('unconfigured');
        this.scheduleWellRetry();
      } else {
        this._wellLoadIssue.set(null);
        this.clearWellRetry();
        this._signals.currentWell.set(applyApiToGui(raw) as Well);
      }
      this.onWellReply();
    });
  }

  /** No-op in HMI mode: adding wells is not supported on the device. */
  addWell(_data: any): Observable<{ success: boolean; id?: string }> {
    return EMPTY;
  }

  /** No-op in HMI mode: the device is hard-wired to a single well, so a list is never needed. */
  subscribeToWellList(): void {
    // No-op: HMI connects to a single well, no list needed
  }

  /** Fetches the well by id and stores it in the currentWell signal.
   *  When called without an id, all signals are reset to null. */
  public setCurrentWellById(id?: string): void {
    this.clearWellRetry();
    if (id) {
      this._signals.currentWell.set({ id } as Well);
      this._lastWellFetchId = id;
      this.requestWell(id);
      this.subscribeToWellEvent(id, APP_CONSTANTS.WELL_EVENTS.WELL_MODIFICATION);
    } else {
      this._lastWellFetchId = null;
      this._wellLoadIssue.set(null);
      const { wells, liveUsers, ...currentWellSignals } = this._signals;
      Object.values(currentWellSignals).forEach((signal) => signal.set(null));
    }
  }

  /** Re-requests the well after _wellRetryDelayMs, unless a retry is already pending or the
   *  well was released in the meantime. Driven by responses, so it stops while disconnected. */
  private scheduleWellRetry(): void {
    if (this._wellRetryTimer !== null) return;
    this._wellRetryTimer = setTimeout(() => {
      this._wellRetryTimer = null;
      const id = this._lastWellFetchId;
      if (id && !this._signals.currentWell()?.purpose) this.requestWell(id);
    }, this._wellRetryDelayMs);
  }

  /** Cancels a pending well retry. */
  private clearWellRetry(): void {
    if (this._wellRetryTimer === null) return;
    clearTimeout(this._wellRetryTimer);
    this._wellRetryTimer = null;
  }

  /** Sends a SET_WELL request using the HMI-specific wellModification payload shape
   *  (differs from KenLive's $set / filter pattern). */
  saveWellInfo(sendData: any): Observable<boolean> {
    this.ws.sendWsRequest(
      { name: APP_CONSTANTS.REQUEST_MESSAGES.SET_WELL },
      {
        wellModification: { ...sendData },
      },
    );
    return this.watchSaveWell();
  }
}
